// src/pages/playlists/playlistsModel.js
// Pure helpers for playlists: labels and colours (from the design handoff),
// item keys, immutable list updates, the type-tile cover layout and formatting.
export const LIMITS = { playlists: 50, items: 500, name: 60, desc: 160 };

// Handoff type colours.
const TYPE_ACCENT = {
  Lecture: "#d96f1e",
  Naat: "#5e7d4f",
  Qirat: "#8b6f47",
  Sama: "#3f6f9e",
  Milaad: "#5e7d4f",
};
const DEFAULT_ACCENT = "#8b6f47";

// Same labels the video archive badges use.
const VIDEO_TYPE_LABEL = {
  "video-lecture": "Lecture",
  milaad: "Milaad",
  samaa: "Sama",
  "annual-event": "Event",
};

export function typeLabel(kind, type) {
  if (kind === "video") return VIDEO_TYPE_LABEL[type] || "Video";
  return type || "Audio";
}

export function accentFor(kind, type) {
  return TYPE_ACCENT[typeLabel(kind, type)] || DEFAULT_ACCENT;
}

export const audioKey = (id) => `a:${id}`;
export const videoKey = (livestreamId) => `v:${livestreamId}`;

export function toggleKey(keys, key) {
  return keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key];
}

export function moveKey(keys, key, dir) {
  const i = keys.indexOf(key);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= keys.length) return keys;
  const next = [...keys];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export const replacePlaylist = (list, p) => list.map((x) => (x.id === p.id ? p : x));
export const prependPlaylist = (list, p) => [p, ...list.filter((x) => x.id !== p.id)];
export const removePlaylist = (list, id) => list.filter((x) => x.id !== id);
export const patchPlaylist = (list, id, patch) => list.map((x) => (x.id === id ? { ...x, ...patch } : x));

export function insertPlaylistAt(list, p, index) {
  const next = list.filter((x) => x.id !== p.id);
  next.splice(Math.max(0, Math.min(index, next.length)), 0, p);
  return next;
}

function tile({ kind, type }) {
  return { kind, type, label: typeLabel(kind, type), accent: accentFor(kind, type) };
}

// Handoff cover: 0 → empty, 1–3 → one tile, 4 → 2×2, 5+ → three tiles + "+N more".
export function coverTiles(coverItems = [], total = 0) {
  if (!total || !coverItems.length) return { layout: "empty", tiles: [] };
  if (total < 4 || coverItems.length < 4) return { layout: "single", tiles: [tile(coverItems[0])] };
  if (total === 4) return { layout: "grid", tiles: coverItems.slice(0, 4).map(tile) };
  return { layout: "grid", tiles: [...coverItems.slice(0, 3).map(tile), { more: total - 3 }] };
}

export function formatLength(secs) {
  const s = Math.max(0, Math.round(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h ? `${h} hr ${m} min` : `${Math.max(1, m)} min`;
}

const pad = (n) => String(n).padStart(2, "0");
export function formatClock(secs) {
  if (secs == null || Number.isNaN(Number(secs))) return "";
  const s = Math.floor(secs);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const x = s % 60;
  return h ? `${h}:${pad(m)}:${pad(x)}` : `${m}:${pad(x)}`;
}

export function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
